import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface AlertThresholds {
  revenue_budget_positive?: number
  revenue_budget_warning?: number
  margin_positive?: number
  margin_warning?: number
  dso_positive?: number
  dso_warning?: number
  overdue_positive?: number
  overdue_warning?: number
  project_margin_positive?: number
  project_margin_warning?: number
  utilization_positive?: number
  utilization_warning?: number
}

export interface TenantSettings {
  // Profilo azienda
  currency?: string
  fiscal_year_start?: number
  service_lines?: string[]
  // Target finanziari
  target_annuale?: number
  target_mensile?: number
  target_margine_pct?: number
  target_margine_commessa_pct?: number
  // Target fatturazione
  target_fatturazione_giorni?: number
  target_dso_giorni?: number
  // Target operativi
  ore_disponibili_mensili?: number
  target_utilization_pct?: number
  recurring_growth_rate?: number
  // Pipeline
  pipeline_weight_confirmed?: number
  pipeline_weight_probable?: number
  // Soglie alert
  alert_thresholds?: AlertThresholds
  // Settore (OB.1)
  industry?: string | null
  // Sistema
  onboarding_completed?: boolean
}

export interface TenantData {
  id: string
  name: string
  slug: string
  plan: string
  settings: TenantSettings
  created_at: string | null
  updated_at: string | null
}

export interface TenantUpdate {
  name?: string
  settings?: Partial<TenantSettings>
}

// Valori di default per le soglie alert (usati se il tenant non ha configurato i propri)
export const DEFAULT_THRESHOLDS: Required<AlertThresholds> = {
  revenue_budget_positive: 90,
  revenue_budget_warning: 70,
  margin_positive: 30,
  margin_warning: 15,
  dso_positive: 30,
  dso_warning: 60,
  overdue_positive: 5,
  overdue_warning: 15,
  project_margin_positive: 25,
  project_margin_warning: 15,
  utilization_positive: 75,
  utilization_warning: 50,
}

export function useTenant(enabled = true) {
  return useQuery<TenantData>({
    queryKey: ['tenant'],
    queryFn: () => api.get<TenantData>('/api/v1/tenants/me'),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateTenant() {
  const queryClient = useQueryClient()
  return useMutation<TenantData, Error, TenantUpdate>({
    mutationFn: (data) => api.patch<TenantData>('/api/v1/tenants/me', data),
    onSuccess: (data) => {
      queryClient.setQueryData(['tenant'], data)
    },
  })
}
